<template>
    <div v-if="store.activeModalTimerId" class="modal sp-modal-card is-active">
        <div class="modal-background" @click="closeModal"></div>
        <div class="modal-card">
            <header class="modal-card-head">
                <template v-if="!isEditing">
                    <p class="modal-card-title" :title="timer?.id">{{ timer?.title }}</p>
                    <div class="buttons">
                        <button class="button is-small" @click="toggleEdit">
                            <span class="icon is-small">
                                <FontAwesomeIcon :icon="faEdit" />
                            </span>
                            <span>{{ isEditing ? 'Cancel' : 'Edit' }}</span>
                        </button>
                        <button class="button is-small" aria-label="close" @click="closeModal">
                            <span class="icon is-small">
                                <FontAwesomeIcon :icon="faXmark" />
                            </span>
                        </button>
                    </div>
                </template>
                <template v-else>
                    <div style="width: 100%;">
                        <div class="field is-grouped mb-0">
                            <p class="control is-expanded" style="position: relative;">
                                <input
                                    ref="editTitleInput"
                                    class="input"
                                    type="text"
                                    v-model="tagParser.inputValue.value"
                                    @keydown="handleEditKeydown"
                                    @input="tagParser.onInput()"
                                    @blur="onEditBlur"
                                >
                                <AutocompleteDropdown
                                    v-if="hasProjectsTags"
                                    :visible="tagParser.showAutocomplete.value"
                                    :items="tagParser.autocompleteItems.value"
                                    :trigger-char="tagParser.autocompleteType.value === 'project' ? '@' : '#'"
                                    :query="tagParser.currentTokenText.value"
                                    :show-create="tagParser.showCreateOption.value"
                                    :selected-index="tagParser.selectedAutocompleteIndex.value"
                                    @select="tagParser.selectItem($event)"
                                    @create="tagParser.createFromToken()"
                                    @hover="tagParser.selectedAutocompleteIndex.value = $event"
                                />
                            </p>
                            <p class="control">
                                <button class="button" @click="saveEdit">
                                    <span class="icon">
                                        <FontAwesomeIcon :icon="faSave" />
                                    </span>
                                    <span>Save</span>
                                </button>
                            </p>
                        </div>
                        <div v-if="hasProjectsTags && tagParser.hasPills.value" class="pill-bar mt-2">
                            <span v-if="tagParser.committedProject.value" class="tag is-medium pill-tag pill-project">
                                @{{ tagParser.committedProject.value.name }}
                                <button type="button" class="delete is-small" @click="tagParser.removeProject()"></button>
                            </span>
                            <span
                                v-for="(tag, index) in tagParser.committedTags.value"
                                :key="tag.name"
                                class="tag is-medium pill-tag pill-tag-item"
                            >
                                #{{ tag.name }}
                                <button type="button" class="delete is-small" @click="tagParser.removeTag(index)"></button>
                            </span>
                        </div>
                    </div>
                </template>
            </header>
            <section class="modal-card-body">
                <div v-if="timer" class="content">
                    <!-- Time Display -->
                    <div class="">

                        <div class="columns mb-0 is-mobile">
                            <div class="column is-narrow">
                                <div class="field has-addons">
                                    <!-- Primary immediate action button -->
                                    <p class="control">
                                        <button
                                            class="button"
                                            :class="isTimerActive ? 'is-link' : 'is-primary'"
                                            :disabled="!timer"
                                            @click="immediateAction"
                                        >
                                            <span class="icon">
                                                <FontAwesomeIcon :icon="isTimerActive ? faStop : faPlay" />
                                            </span>
                                            <span>{{ isTimerActive ? 'Stop Timer' : 'Start Timer' }}</span>
                                        </button>
                                    </p>
                                    <!-- Dropdown button for backdating/custom times (only when backend available) -->
                                    <p class="control" v-if="!store.isFrontendOnly">
                                        <TimerControlDropdown
                                            :timer-id="timer.id"
                                            :action-type="isTimerActive ? 'stop' : 'start'"
                                            omit-immediate
                                            dropdown-button-only
                                            @action-completed="handleTimerAction"
                                            @error="handleTimerError"
                                        />
                                    </p>
                                </div>
                            </div>
                            <div class="column">
                                <div>
                                    <p class="mb-0 has-text-weight-medium">{{ timer?.title }}</p>
                                    <p v-if="projectTagsLabel" class="mb-0 has-text-grey is-size-7">{{ projectTagsLabel }}</p>
                                    <p>
                                        <small>
                                            {{ formatTime(new Date(timer.startDate)) }} - {{ formatTime(new
                                                Date(timer.endDate)) }} ({{ formatBlockDuration(timer) }})
                                        </small>
                                    </p>
                                </div>
                            </div>
                            <div class="column has-text-right">
                                <div class="" v-if="isTimerActive">
                                    <p class="mb-0" v-html="formatBoldTimecode(store.elapsedTimeInSeconds)"></p>
                                    <p class=""><small
                                            v-html="formatRemainingTime(store.remainingTimeInSeconds)"></small></p>
                                </div>

                            </div>
                        </div>

                        <!-- Progress Bar -->
                        <progress class="progress mt-2" :class="{
                            'is-primary': progressPercentage < 100,
                            'is-success': progressPercentage >= 100
                        }" :value="progressPercentage" max="100"></progress>
                    </div>
                </div>
                
                <div v-if="showNotesSection" class="timer-notes mt-4">
                    <div v-show="isNotesEditing" class="notes-editor">
                        <textarea ref="noteTextarea"></textarea>
                        <div class="buttons are-small mt-3">
                            <button
                                class="button is-primary"
                                @click="saveNotes"
                            >
                                <span class="icon is-small">
                                    <FontAwesomeIcon :icon="faSave" />
                                </span>
                                <span>Save</span>
                            </button>
                            <button
                                class="button"
                                @click="cancelNotesEdit"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                    <div
                        v-if="!isNotesEditing && timer?.notes"
                        class="notes-read content"
                        :class="{ 'is-clickable': hasTimerNotes }"
                        @click="hasTimerNotes ? startNotesEdit() : null"
                        v-html="noteHtml"
                    ></div>
                    <div
                        v-else-if="!isNotesEditing && hasTimerNotes"
                        class="notes-placeholder"
                        @click="startNotesEdit"
                    >
                        Add a short note for this timer.
                    </div>
                </div>
                
                <!-- Events List -->
                <div v-show="isEventsOpen" class="events-content mt-4">
                    <!-- Frontend-only mode message -->
                    <div v-if="store.isFrontendOnly" class="has-text-grey has-text-centered py-4">
                        <p>Create an account to track individual sessions.</p>
                    </div>
                    <!-- Regular events view for authenticated users -->
                    <template v-else>
                        <div v-if="isLoadingEvents" class="has-text-centered py-4">
                            <span class="icon">
                                <FontAwesomeIcon :icon="faSpinner" spin />
                            </span>
                        </div>
                        <div v-else-if="events.length === 0" class="has-text-grey has-text-centered py-4">
                            No events recorded yet
                        </div>
                        <div v-else class="events-list">
                            <div v-for="event in events" :key="event.id" class="event-item py-2">
                                <div class="columns is-mobile is-gapless mb-0 is-vcentered">
                                    <div class="column">
                                        <p class="is-size-7">
                                            {{ formatTime(new Date(event.started_at)) }}
                                            <span v-if="!isSameDay(event.started_at)" class="has-text-grey">
                                                ({{ formatEventDate(event.started_at) }})
                                            </span>
                                            <span class="has-text-grey">→</span>
                                            {{ event.ended_at ? formatTime(new Date(event.ended_at)) : 'ongoing' }}
                                        </p>
                                    </div>
                                    <div class="column is-narrow">
                                        <p class="is-size-7 has-text-grey ml-4">
                                            {{ event.duration ? formatDuration(event.duration) : '—' }}
                                        </p>
                                    </div>
                                    <div class="column is-narrow" v-if="event.ended_at">
                                        <button 
                                            class="button is-small is-text" 
                                            @click="openEventEditModal(event)"
                                            title="Edit event"
                                        >
                                            <span class="icon is-small">
                                                <FontAwesomeIcon :icon="faEdit" />
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <!-- Move Panel -->
                <div v-show="isMoveOpen" class="move-content mt-4">
                    <div class="mb-4">
                        <TimePicker
                            v-model="moveStartDate"
                            label="Start Time"
                            :min-date="sessionDisplayStart"
                            :max-date="sessionDisplayEnd"
                        />
                    </div>

                    <div class="field">
                        <label class="label">Duration</label>
                        <div class="field is-grouped">
                            <p class="control">
                                <input
                                    class="input"
                                    type="number"
                                    v-model.number="moveHours"
                                    min="0"
                                    max="29"
                                    style="width: 5rem;"
                                />
                            </p>
                            <p class="control">
                                <span class="button is-static">h</span>
                            </p>
                            <p class="control">
                                <span class="select">
                                    <select v-model.number="moveMinutes">
                                        <option v-for="m in [0,5,10,15,20,25,30,35,40,45,50,55]" :key="m" :value="m">
                                            {{ String(m).padStart(2, '0') }}
                                        </option>
                                    </select>
                                </span>
                            </p>
                            <p class="control">
                                <span class="button is-static">m</span>
                            </p>
                        </div>
                    </div>

                    <p v-if="moveError" class="help has-text-danger mb-3">{{ moveError }}</p>

                    <button class="button is-primary" @click="saveMove">
                        <span class="icon"><FontAwesomeIcon :icon="faSave" /></span>
                        <span>Save</span>
                    </button>
                </div>
            </section>
            <footer class="modal-card-foot">
                <div class="buttons">
                    <button class="button is-hidden-desktop" @click="toggleMove">
                        <span class="icon">
                            <FontAwesomeIcon :icon="faUpDown" />
                        </span>
                        <span>Move</span>
                    </button>
                    <button class="button" @click="toggleEvents">
                        <span class="icon">
                            <FontAwesomeIcon :icon="faHistory" />
                        </span>
                        <span>Sessions</span>
                    </button>
                    <button class="button" @click="openFocusMode" title="Focus mode">
                        <span class="icon">
                            <FontAwesomeIcon :icon="faExpand" />
                        </span>
                    </button>
                    <button class="button is-danger is-outlined" @click="deleteTimer">
                        <span class="icon">
                            <FontAwesomeIcon :icon="faTrash" />
                        </span>
                    </button>
                </div>
            </footer>
        </div>
    </div>

    <!-- Event Edit Modal -->
    <EventEditModal
        :is-open="showEventEditModal"
        :event="eventToEdit"
        :timer-name="timer?.title"
        @save="handleEventSave"
        @cancel="closeEventEditModal"
        @delete="handleEventDelete"
    />
</template>

<script setup>
import { computed, ref, nextTick, watch } from 'vue';
import { useBlocksStore } from '../stores/blocks';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlay, faStop, faEdit, faSave, faTrash, faXmark, faSpinner, faHistory, faUpDown, faExpand } from '@fortawesome/free-solid-svg-icons';
import { formatBoldTimecode, formatDuration, formatTime, formatBlockDuration, calculateRemainingTime, formatRemainingTime } from '../utils/time';
import { storeToRefs } from 'pinia';
import axios from 'axios';
import TimerControlDropdown from './TimerControlDropdown.vue';
import EventEditModal from './EventEditModal.vue';
import TimePicker from './TimePicker.vue';
import AutocompleteDropdown from './AutocompleteDropdown.vue';
import { useOmniboxParser } from '../composables/useOmniboxParser';
import { useMarkdownEditor } from '../composables/useMarkdownEditor';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const store = useBlocksStore();

const { blocks } = storeToRefs(store);

const timer = computed(() =>
    blocks.value.find(block => block.id === store.activeModalTimerId)
);

const hasTimerNotes = computed(() => store.hasEntitlement('timer_notes'));

const isTimerActive = computed(() =>
    timer.value?.isActive
);

const projectTagsLabel = computed(() => {
    if (!timer.value) return ''
    const parts = []
    if (timer.value.project) parts.push(`@${timer.value.project.name}`)
    if (timer.value.tags?.length) parts.push(...timer.value.tags.map(t => `#${t.name}`))
    return parts.join(' ')
});

const hasProjectsTags = computed(() => store.hasEntitlement('projects_tags'))

const notesDraft = ref('');
const isNotesEditing = ref(false);
const noteTextarea = ref(null);
const { editor: noteEditor } = useMarkdownEditor(noteTextarea, notesDraft, { minHeight: '110px', maxHeight: '200px' });

const noteHtml = computed(() => DOMPurify.sanitize(marked.parse(timer.value?.notes || '', { breaks: true })));
const showNotesSection = computed(() => hasTimerNotes.value || !!timer.value?.notes);

// Add key bindings once editor is mounted
watch(noteEditor, (instance) => {
    if (!instance) return;
    instance.codemirror.addKeyMap({
        'Cmd-Enter': () => saveNotes(),
        'Ctrl-Enter': () => saveNotes(),
        'Esc': () => cancelNotesEdit(),
    });
});

// --- Projects/Tags editor (in header edit mode) ---
const tagParser = useOmniboxParser(store)
const tagsInput = ref(null)

const toggleEdit = async () => {
    isEditing.value = !isEditing.value
    if (isEditing.value) {
        // Populate parser with current title + project/tags
        tagParser.reset()
        tagParser.inputValue.value = timer.value?.title || ''
        if (timer.value?.project) {
            tagParser.committedProject.value = { ...timer.value.project }
        }
        if (timer.value?.tags?.length) {
            tagParser.committedTags.value = timer.value.tags.map(t => ({ ...t }))
        }
        await nextTick()
        if (editTitleInput.value) {
            editTitleInput.value.focus()
            const len = tagParser.inputValue.value.length
            try { editTitleInput.value.setSelectionRange(len, len) } catch (e) {}
        }
    }
}

function handleEditKeydown(e) {
    if (e.key === 'Escape') {
        e.preventDefault()
        cancelEdit()
        return
    }

    if (hasProjectsTags.value) {
        tagParser.onKeydown(e)
        if (e.defaultPrevented) return
    }

    if (e.key === 'Enter') {
        e.preventDefault()
        saveEdit()
    }
}

function onEditBlur() {
    setTimeout(() => tagParser.cancelToken({ removeFromInput: true }), 150)
}

async function saveEdit() {
    if (hasProjectsTags.value) {
        tagParser.finalizeActiveToken()
    }

    const payload = tagParser.getPayload()
    const title = payload.title
    if (!title) return

    const updateData = {}

    // Title change
    if (title !== timer.value?.title) {
        updateData.title = title
    }

    // Project/tag changes (Plus only)
    if (hasProjectsTags.value) {
        if (payload.projectId) updateData.projectId = payload.projectId
        else if (payload.projectName) updateData.projectName = payload.projectName
        else updateData.projectId = null

        updateData.tagIds = payload.tagIds || []
        updateData.tagNames = payload.tagNames || []
    }

    // Skip if nothing changed
    if (Object.keys(updateData).length === 0) {
        isEditing.value = false
        return
    }

    try {
        await store.updateTimer(timer.value.id, updateData)
        isEditing.value = false
    } catch (error) {
        console.error('Failed to update timer', error)
    }
}

const cancelEdit = () => {
    tagParser.reset()
    isEditing.value = false
}

watch(timer, (next) => {
    notesDraft.value = next?.notes || '';
    isNotesEditing.value = false;
});

const startNotesEdit = async () => {
    if (!hasTimerNotes.value) return;
    notesDraft.value = timer.value?.notes || '';
    isNotesEditing.value = true;
    await nextTick();
    if (noteEditor.value) {
        noteEditor.value.codemirror.refresh();
        noteEditor.value.codemirror.focus();
    }
};

const cancelNotesEdit = () => {
    isNotesEditing.value = false;
    notesDraft.value = timer.value?.notes || '';
};

const saveNotes = async () => {
    if (!hasTimerNotes.value || !timer.value) return;
    const notes = notesDraft.value;
    isNotesEditing.value = false;
    try {
        await store.updateTimer(timer.value.id, { notes });
    } catch (error) {
        console.error('Failed to update notes', error);
    }
};

const timerElapsedSeconds = computed(() => {
    if (!timer.value) return 0;
    if (timer.value.isActive) {  // Fix: Use timer.value.isActive
        return store.elapsedTimeInSeconds;
    } else {
        return timer.value.duration || 0;
    }
});

const remainingTimeInSeconds = computed(() => {
    if (!timer.value) return 0;
    if (timer.value.isActive) {
        return store.remainingTimeInSeconds;
    } else {
        return -calculateRemainingTime(timer.value);
    }
});

const progressPercentage = computed(() => {
    if (!timer.value) return 0;

    const totalDurationMs = timer.value.endDate - timer.value.startDate;
    const elapsedMs = isTimerActive.value ? store.elapsedTimeInSeconds * 1000 : (timer.value.duration || 0) * 1000;

    const exactPercentage = (elapsedMs / totalDurationMs) * 100;
    
    // Only return 100% if we've truly exceeded the scheduled time
    // Use Math.floor to avoid rounding up too early, and cap at 100
    return Math.min(100, exactPercentage >= 100 ? 100 : Math.floor(exactPercentage));
});

const startTimer = () => {
    if (timer.value) {
        store.startTimer(timer.value.id);
    }
};

const stopTimer = async () => {
    if (timer.value) {
        await store.stopTimer(timer.value.id);
        if (isEventsOpen.value) {
            // Reload events immediately if they're visible
            await loadEvents();
        } else {
            // Mark for refresh next time they're opened
            needsEventsRefresh.value = true;
        }
    }
};

// Immediate action for primary button (start now or stop now)
const immediateAction = async () => {
    if (!timer.value) return;
    if (isTimerActive.value) {
        await stopTimer();
    } else {
        await store.startTimer(timer.value.id);
        // Starting may create a new running event; mark events for refresh
        if (isEventsOpen.value) {
            await loadEvents();
        } else {
            needsEventsRefresh.value = true;
        }
    }
};

// Handle timer actions from dropdown
const handleTimerAction = async (result) => {
    console.log('Timer action completed:', result);
    
    // Refresh events if they're currently open
    if (isEventsOpen.value) {
        await loadEvents();
    } else {
        // Mark for refresh next time they're opened
        needsEventsRefresh.value = true;
    }
};

// Handle timer errors from dropdown
const handleTimerError = (error) => {
    console.error('Timer action error:', error);
    // Error is already displayed by the dropdown component
};

const deleteTimer = () => {
    if (timer.value) {
        if (confirm('Are you sure you want to delete this timer?')) {
            store.deleteTimer(timer.value.id);
        }
    }
};

const isEditing = ref(false)
const editTitleInput = ref(null)

const events = ref([]);
const isLoadingEvents = ref(false);
const isEventsOpen = ref(false);
const needsEventsRefresh = ref(false);
const lastLoadedTimerId = ref(null);

// Event editing state
const showEventEditModal = ref(false);
const eventToEdit = ref(null);

// Move panel state
const isMoveOpen = ref(false);
const moveStartDate = ref('');
const moveHours = ref(0);
const moveMinutes = ref(0);
const moveError = ref('');

// Session display boundaries (mirrors CalendarView.vue logic)
const sessionDisplayStart = computed(() => {
    const baseDate = store.userSession?.initializedAt
        ? new Date(store.userSession.initializedAt)
        : new Date();
    if (store.startTime !== null) {
        const d = new Date(baseDate);
        d.setHours(0, store.startTime, 0, 0);
        return d;
    }
    return baseDate;
});

const sessionDisplayEnd = computed(() =>
    new Date(sessionDisplayStart.value.getTime() + 30 * 60 * 60 * 1000)
);

const toggleMove = () => {
    if (!isMoveOpen.value && isEventsOpen.value) {
        isEventsOpen.value = false;
    }
    isMoveOpen.value = !isMoveOpen.value;
    if (isMoveOpen.value) {
        initializeMoveFields();
    }
    moveError.value = '';
};

const initializeMoveFields = () => {
    if (!timer.value) return;
    moveStartDate.value = timer.value.startDate.toISOString();
    const durationMs = timer.value.endDate - timer.value.startDate;
    const totalMinutes = Math.round(durationMs / 60000);
    moveHours.value = Math.floor(totalMinutes / 60);
    moveMinutes.value = Math.round((totalMinutes % 60) / 5) * 5;
};

const saveMove = async () => {
    if (!timer.value) return;
    moveError.value = '';

    const start = new Date(moveStartDate.value);
    const durationMs = (moveHours.value * 60 + moveMinutes.value) * 60 * 1000;

    if (durationMs <= 0) {
        moveError.value = 'Duration must be greater than zero.';
        return;
    }

    const end = new Date(start.getTime() + durationMs);

    if (start < sessionDisplayStart.value) {
        moveError.value = 'Start time cannot be before the session start.';
        return;
    }

    if (end > sessionDisplayEnd.value) {
        moveError.value = 'Timer would extend beyond the session window.';
        return;
    }

    try {
        await store.updateTimer(timer.value.id, {
            startDate: start,
            endDate: end,
        });
        isMoveOpen.value = false;
    } catch (error) {
        moveError.value = 'Failed to update timer.';
        console.error('Move save error:', error);
    }
};

const toggleEvents = async () => {
    if (!isEventsOpen.value && isMoveOpen.value) {
        isMoveOpen.value = false;
    }
    isEventsOpen.value = !isEventsOpen.value;
    if (isEventsOpen.value && (events.value.length === 0 || needsEventsRefresh.value || lastLoadedTimerId.value !== timer.value?.id)) {
        await loadEvents();
        needsEventsRefresh.value = false;
    }
};

const loadEvents = async () => {
    isLoadingEvents.value = true;
    try {
        const { data } = await axios.get(`/api/timers/${timer.value.id}/events`);
        events.value = data.events;
        lastLoadedTimerId.value = timer.value.id;
    } catch (error) {
        console.error('Error loading timer events:', error);
    } finally {
        isLoadingEvents.value = false;
    }
};

const isSameDay = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
};

const formatEventDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric'
    });
};

// Event editing methods
const openEventEditModal = (event) => {
    eventToEdit.value = event;
    showEventEditModal.value = true;
};

const closeEventEditModal = () => {
    showEventEditModal.value = false;
    eventToEdit.value = null;
};

const handleEventSave = async (saveData) => {
    try {
        const result = await store.updateEvent(
            saveData.eventId, 
            saveData.data, 
            saveData.force
        );

        if (result.success) {
            // Success - close modal and refresh events
            closeEventEditModal();
            await loadEvents();
        } else {
            // Handle specific error cases
            if (result.error === 'significant_change') {
                // The EventEditModal will handle this by showing confirmation
                console.log('Significant change detected, will show confirmation');
            } else {
                // Other errors - could show notification
                console.error('Event save error:', result.message);
            }
        }
    } catch (error) {
        console.error('Error handling event save:', error);
    }
};

const handleEventDelete = async (eventId) => {
    try {
        const result = await store.deleteEvent(eventId);

        if (result.success) {
            // Success - close modal and refresh events
            closeEventEditModal();
            await loadEvents();
        } else {
            // Handle error
            console.error('Event delete error:', result.message);
        }
    } catch (error) {
        console.error('Error handling event delete:', error);
    }
};

const closeModal = () => {
    isEditing.value = false;
    tagParser.reset();
    isEventsOpen.value = false;
    isMoveOpen.value = false;
    moveError.value = '';
    events.value = [];
    lastLoadedTimerId.value = null;
    needsEventsRefresh.value = false;
    store.hideModal();
};

const openFocusMode = () => {
    store.openFocusMode(timer.value?.id || null);
    closeModal();
};
</script>

<style scoped>
.progress {
    margin-top: 1rem;
    height: 0.5rem;
}

.modal-card-body {
    transition: height 0.2s ease-out;
    /* Allow dropdown menus or other popovers to escape the body */
    overflow: visible;
}

.events-list {
    background-color: var(--bulma-scheme-main-bis);
    border-radius: var(--bulma-radius);
    padding: 0.5rem;
}

.event-item {
    border-bottom: 1px solid var(--bulma-scheme-main-ter);
}

.event-item:last-child {
    border-bottom: none;
}

summary {
  cursor: pointer;
  user-select: none;
}

summary:hover {
  color: var(--bulma-link);
}

.events-content {
    border-top: 1px solid var(--bulma-scheme-main-ter);
    padding-top: 1rem;
    transition: all 0.5s ease-out;
    /* Let dropdown / tooltips render beyond area */
    overflow: visible;
}

.move-content {
    border-top: 1px solid var(--bulma-scheme-main-ter);
    padding-top: 1rem;
    transition: all 0.5s ease-out;
    overflow: visible;
}

.pill-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 8px 12px;
    background: var(--bulma-scheme-main-bis);
    border-radius: 8px;
}

.pill-tag {
    border-radius: 12px;
    font-size: 0.8rem;
}

.pill-project {
    background: var(--bulma-link-light);
    color: var(--bulma-link-dark);
}

.pill-tag-item {
    background: var(--bulma-info-light);
    color: var(--bulma-info-dark);
}

.timer-notes {
    border-top: 1px solid var(--bulma-scheme-main-ter);
    padding-top: 1rem;
}

.notes-editor :deep(.CodeMirror) {
    min-height: 140px;
}

.notes-read {
    background: var(--bulma-scheme-main-bis);
    border-radius: 8px;
    padding: 1rem;
    line-height: 1.5;
    font-size: 0.95rem;
    color: var(--bulma-text);
}

.notes-read :deep(*:first-child) { margin-top: 0; }
.notes-read :deep(*:last-child) { margin-bottom: 0; }
.notes-read :deep(h1),
.notes-read :deep(h2),
.notes-read :deep(h3),
.notes-read :deep(h4),
.notes-read :deep(h5),
.notes-read :deep(h6) {
    color: var(--bulma-text-strong);
    font-weight: 700;
    line-height: 1.2;
    margin: 1rem 0 0.6rem;
}
.notes-read :deep(h1) { font-size: 1.55rem; }
.notes-read :deep(h2) { font-size: 1.3rem; }
.notes-read :deep(h3) { font-size: 1.1rem; }
.notes-read :deep(h4) { font-size: 1rem; }
.notes-read :deep(h5),
.notes-read :deep(h6) {
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}

.notes-read :deep(p) {
    margin-bottom: 0.75em;
}

.notes-read :deep(ul),
.notes-read :deep(ol) {
    margin: 0.4em 0 0.8em 1.4em;
    padding-left: 0.2em;
}

.notes-read :deep(ul) {
    list-style: disc;
}

.notes-read :deep(ol) {
    list-style: decimal;
}

.notes-read :deep(li) {
    margin-bottom: 0.2em;
}

.notes-read :deep(strong) { font-weight: 600; }
.notes-read :deep(em) { font-style: italic; }

.notes-read :deep(blockquote) {
    margin: 0.75em 0;
    padding: 0.1em 0 0.1em 0.9em;
    border-left: 3px solid var(--bulma-border);
    color: var(--bulma-text-weak);
}

.notes-read :deep(hr) {
    margin: 1rem 0;
    background: var(--bulma-border);
}

.notes-read :deep(code) {
    background: var(--bulma-scheme-main-ter);
    border-radius: 3px;
    padding: 0.1em 0.3em;
    font-size: 0.85em;
    font-family: monospace;
}

.notes-read :deep(pre) {
    background: var(--bulma-scheme-main-ter);
    border-radius: 4px;
    padding: 0.5em 0.75em;
    overflow-x: auto;
    margin: 0.5em 0;
}

.notes-read :deep(pre code) { background: none; padding: 0; }

.notes-read :deep(a) {
    color: var(--bulma-link);
    text-decoration: underline;
}

.notes-read :deep(table) {
    width: 100%;
    margin: 0.75em 0;
    border-collapse: collapse;
}

.notes-read :deep(th),
.notes-read :deep(td) {
    padding: 0.45em 0.6em;
    border: 1px solid var(--bulma-border);
    text-align: left;
}

.notes-read :deep(th) {
    color: var(--bulma-text-strong);
    background: var(--bulma-scheme-main-ter);
}

.notes-read.is-clickable {
    cursor: pointer;
}

.notes-placeholder {
    color: var(--bulma-text-weak);
    cursor: pointer;
}

/* Ensure nested dropdown menus/portals inside the modal are not clipped
   Using :deep to pierce scoped boundary for Bulma structural classes */
:deep(.modal-card) {
    overflow: visible; /* override Bulma's overflow hidden */
}

:deep(.modal-card .dropdown-menu) {
    z-index: 40; /* higher than local 30 to clear other elements */
}
</style>
